// frontend/src/components/NotificationsBell/NotificationsBell.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Popover, List, Typography, Spin, Empty, Button, Tooltip, Tabs, App, Drawer } from 'antd';
import {
    BellOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    CheckOutlined,
    MailOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import * as notificationService from '../../services/notificationService';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Text, Paragraph } = Typography;

const FILTER_TODAS = 'todas';
const FILTER_NAO_LIDAS = 'nao_lidas';
const FILTER_LIDAS = 'lidas';

const NotificationsBell = ({ isMobile: isMobileProp }) => {
    const { message, modal } = App.useApp();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(false);
    const [filter, setFilter] = useState(FILTER_TODAS);
    const { refreshKey } = useDataRefresh();
    const navigate = useNavigate();

    const [isMobileLocal, setIsMobileLocal] = useState(window.innerWidth <= 768);
    const isMobile = typeof isMobileProp === 'boolean' ? isMobileProp : isMobileLocal;

    useEffect(() => {
        if (typeof isMobileProp === 'boolean') return undefined;
        const handleResize = () => {
            setIsMobileLocal(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileProp]);

    const unreadCount = notifications.filter(n => !n.lida).length;

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                setLoading(true);
                const response = await notificationService.getNotifications();
                setNotifications(response.data);
            } catch (error) {
                console.error("Erro ao buscar notificações:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchNotifications();
    }, [refreshKey]);

    const handleNotificationClick = async (notification) => {
        if (!notification.lida) {
            try {
                await notificationService.markAsRead(notification.id);
                setNotifications(prev => 
                    prev.map(n => n.id === notification.id ? { ...n, lida: true } : n)
                );
            } catch (error) {
                console.error("Erro ao marcar notificação como lida:", error);
            }
        }
        
        if (notification.url_destino) {
            if (notification.url_destino.startsWith('http')) {
                window.open(notification.url_destino, '_blank', 'noopener,noreferrer');
            } else {
                navigate(notification.url_destino);
            }
        }
        
        setVisible(false);
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, lida: true })));
            message.success('Todas as notificações foram marcadas como lidas');
        } catch (error) {
            console.error("Erro ao marcar todas as notificações como lidas:", error);
        }
    };

    const handleMarkAsRead = async (e, notification) => {
        e.stopPropagation();
        try {
            await notificationService.markAsRead(notification.id);
            setNotifications(prev =>
                prev.map(n => (n.id === notification.id ? { ...n, lida: true } : n))
            );
            message.success('Notificação marcada como lida');
        } catch (error) {
            console.error("Erro ao marcar notificação como lida:", error);
        }
    };

    const handleMarkAsUnread = async (e, notification) => {
        e.stopPropagation();
        try {
            await notificationService.markAsUnread(notification.id);
            setNotifications(prev =>
                prev.map(n => (n.id === notification.id ? { ...n, lida: false } : n))
            );
            message.success('Notificação marcada como não lida');
        } catch (error) {
            console.error("Erro ao marcar notificação como não lida:", error);
        }
    };

    const openDeleteConfirm = (notification) => {
        const notificationId = String(notification.id);
        modal.confirm({
            title: 'Excluir notificação',
            content: 'Tem certeza que deseja excluir esta notificação?',
            okText: 'Excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await notificationService.deleteNotification(notificationId);
                    setNotifications(prev => prev.filter(n => String(n.id) !== notificationId));
                    message.success('Notificação excluída');
                } catch (error) {
                    console.error("Erro ao excluir notificação:", error);
                    message.error('Não foi possível excluir a notificação');
                }
            },
        });
    };

    const stopEvent = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const filteredNotifications =
        filter === FILTER_NAO_LIDAS
            ? notifications.filter(n => !n.lida)
            : filter === FILTER_LIDAS
              ? notifications.filter(n => n.lida)
              : notifications;

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'ALERTA':
                return <WarningOutlined style={{ color: '#E6A23C', fontSize: 18 }} />;
            case 'SUGESTAO':
                return <InfoCircleOutlined style={{ color: '#50B0A0', fontSize: 18 }} />;
            case 'AVISO':
            default:
                return <CheckCircleOutlined style={{ color: '#2F9E6F', fontSize: 18 }} />;
        }
    };

    const content = (
        <div
            style={{
                width: isMobile ? '85vw' : 350,
                maxWidth: isMobile ? 320 : 350,
                maxHeight: '400px',
                overflowY: 'auto',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 16px',
                    borderBottom: '1px solid #f0f0f0',
                }}
            >
                <Text strong>Notificações</Text>
                {unreadCount > 0 && (
                    <Button type="link" onClick={handleMarkAllAsRead} size="small">
                        Marcar todas como lidas
                    </Button>
                )}
            </div>
            <Tabs
                activeKey={filter}
                onChange={setFilter}
                size="small"
                style={{ padding: '0 12px', marginBottom: 0 }}
                items={[
                    { key: FILTER_TODAS, label: 'Todas' },
                    { key: FILTER_NAO_LIDAS, label: 'Não lidas' },
                    { key: FILTER_LIDAS, label: 'Lidas' },
                ]}
            />
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                    <Spin />
                </div>
            ) : filteredNotifications.length > 0 ? (
                <List
                    itemLayout="horizontal"
                    dataSource={filteredNotifications}
                    renderItem={item => (
                        <List.Item
                            style={{
                                padding: 0,
                                cursor: 'default',
                                backgroundColor: item.lida ? 'rgba(0,0,0,0.02)' : '#e6f4ff',
                                borderLeft: item.lida ? '3px solid transparent' : '3px solid #50B0A0',
                                transition: 'background-color 0.2s, border-color 0.2s',
                            }}
                            className={`notification-item ${item.lida ? 'notification-item--read' : 'notification-item--unread'}`}
                        >
                            <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start', padding: '12px 16px', gap: 12 }}>
                                <div
                                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                    onClick={() => handleNotificationClick(item)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(item); } }}
                                >
                                    <List.Item.Meta
                                        avatar={getNotificationIcon(item.tipo)}
                                        title={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                {!item.lida && (
                                                    <span
                                                        style={{
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: '#50B0A0',
                                                            background: 'rgba(24, 144, 255, 0.15)',
                                                            padding: '2px 6px',
                                                            borderRadius: 4,
                                                        }}
                                                    >
                                                        Não lida
                                                    </span>
                                                )}
                                                <Text strong={!item.lida} style={{ opacity: item.lida ? 0.85 : 1 }}>
                                                    {item.titulo}
                                                </Text>
                                            </span>
                                        }
                                        description={
                                            <>
                                                <Paragraph
                                                    style={{
                                                        marginBottom: 4,
                                                        fontSize: '13px',
                                                        opacity: item.lida ? 0.9 : 1,
                                                    }}
                                                >
                                                    {item.descricao}
                                                </Paragraph>
                                                <Tooltip
                                                    title={dayjs(item.data_criacao).format(
                                                        'DD/MM/YYYY, HH:mm'
                                                    )}
                                                >
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                                        {dayjs(item.data_criacao).fromNow()}
                                                    </Text>
                                                </Tooltip>
                                            </>
                                        }
                                    />
                                </div>
                                <div
                                    role="group"
                                    aria-label="Ações da notificação"
                                    onClick={stopEvent}
                                    onMouseDown={stopEvent}
                                    onPointerDown={stopEvent}
                                    style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                                >
                                    {!item.lida ? (
                                        <Tooltip title="Marcar como lida">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CheckOutlined />}
                                                onClick={e => { stopEvent(e); handleMarkAsRead(e, item); }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Marcar como não lida">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<MailOutlined />}
                                                onClick={e => { stopEvent(e); handleMarkAsUnread(e, item); }}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Excluir">
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => openDeleteConfirm(item)}
                                        />
                                    </Tooltip>
                                </div>
                            </div>
                        </List.Item>
                    )}
                />
            ) : (
                <Empty
                    description={
                        filter === FILTER_TODAS
                            ? 'Você não tem notificações'
                            : filter === FILTER_NAO_LIDAS
                              ? 'Nenhuma notificação não lida'
                              : 'Nenhuma notificação lida'
                    }
                    style={{ padding: '24px' }}
                />
            )}
        </div>
    );

    // CSS para a animação do sino
    const shakeStyle = `
        @keyframes bellShake {
            0% { transform: rotate(0); }
            5% { transform: rotate(20deg); }
            10% { transform: rotate(-20deg); }
            15% { transform: rotate(10deg); }
            20% { transform: rotate(-10deg); }
            25% { transform: rotate(5deg); }
            30% { transform: rotate(0); }
            100% { transform: rotate(0); }
        }
        .bell-active {
            animation: bellShake 4s infinite;
            transform-origin: top center;
            color: #50B0A0 !important; /* Cor de destaque quando tem notificação */
        }
        .notification-item:hover {
            background-color: rgba(80, 176, 160, 0.10) !important;
        }
        .notification-item.notification-item--read:hover {
            background-color: var(--color-surface-muted, #F8F4EC) !important;
        }
        .notification-item .ant-list-item-meta {
            align-items: flex-start;
        }
        
        /* Ajuste fino para o popover no mobile */
        @media (max-width: 768px) {
            .ant-popover {
                max-width: 90vw !important;
                left: 5% !important; /* Tenta centralizar um pouco se o AntD deixar */
            }
            .ant-popover-content {
                width: 100%;
            }
        }
    `;

    return (
        <>
            <style>{shakeStyle}</style>
            {isMobile ? (
                <>
                    <button
                        type="button"
                        onClick={() => setVisible(true)}
                        aria-label="Notificações"
                        style={{
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 44,
                            minHeight: 44,
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                        }}
                    >
                        <Badge count={unreadCount} overflowCount={99} size="small" offset={[2, 0]}>
                            <BellOutlined
                                className={unreadCount > 0 ? 'bell-active' : ''}
                                style={{
                                    fontSize: '24px',
                                    color: 'var(--color-text-primary)',
                                }}
                            />
                        </Badge>
                    </button>
                    <Drawer
                        open={visible}
                        onClose={() => setVisible(false)}
                        placement="bottom"
                        height="80dvh"
                        title="Notificações"
                        styles={{
                            body: {
                                padding: 0,
                                paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                                overflowY: 'auto',
                            },
                            content: { borderRadius: '16px 16px 0 0' },
                        }}
                    >
                        {content}
                    </Drawer>
                </>
            ) : (
                <Popover
                    content={content}
                    trigger="click"
                    open={visible}
                    onOpenChange={setVisible}
                    placement="bottomRight"
                    arrow={false}
                    overlayStyle={{
                        padding: 0,
                        maxWidth: 'auto',
                    }}
                >
                    <div style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '0 8px', minHeight: 44 }}>
                        <Badge count={unreadCount} overflowCount={99} size="small" offset={[2, 0]}>
                            <BellOutlined
                                className={unreadCount > 0 ? 'bell-active' : ''}
                                style={{
                                    fontSize: '24px',
                                    color: 'var(--color-text-primary)',
                                }}
                            />
                        </Badge>
                    </div>
                </Popover>
            )}
        </>
    );
};

export default NotificationsBell;